import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, ColTypeDef, FilterChangedEvent, FirstDataRenderedEvent, GridApi, GridReadyEvent, RowSelectionOptions, SideBarDef, SizeColumnsToContentStrategy, themeBalham } from 'ag-grid-enterprise';
import { FileNameService } from '../../../core/services';
import type { SupportedColumnType } from '../report-column';
import { ReportWorkspaceService } from '../report-workspace.service';
import { ExportOptions } from '../reporting.model';
import { comparators } from './comparators';
import { CheckboxRendererComponent } from './renderers/checkbox-renderer.component';
import { ColumnFilters } from './report-grid.model';
import { ReportGridService } from './report-grid.service';
import { valueFormatters } from './value-formatters';

@Component({
    selector: 'app-report-grid',
    imports: [AgGridAngular],
    templateUrl: './report-grid.component.html',
    styleUrl: './report-grid.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportGridComponent implements OnInit {
    private reportWorkspace = inject(ReportWorkspaceService);
    private gridService = inject(ReportGridService);
    private fileNameService = inject(FileNameService);
    
    private gridApi!: GridApi;
    private gridReady = signal(false);

    reportDefinition = this.reportWorkspace.reportDefinition;
    reportData = this.reportWorkspace.reportData;
    isReportRunning = this.reportWorkspace.isReportRunning;

    isSysAdmin = this.reportWorkspace.isSysAdmin;
    subscriber = this.reportWorkspace.subscriber;

    columnDefinitions = this.reportWorkspace.activeColumnDefinitions;
    columnGroupDefinitions = this.reportWorkspace.columnGroupDefinitions;
    columnQueryDefaults = this.reportWorkspace.queryColumnDefaults;

    initialColumnFilters = signal<ColumnFilters | null>(null);
    
    colDefs = signal<ColDef[]>([]);
       
    reportName = computed(() => this.reportWorkspace.reportName() ?? '');
    layoutOptions = this.reportWorkspace.layoutOptions;


    /*** Grid Options ***/
    autoSizeStrategy: SizeColumnsToContentStrategy = {
        type: 'fitCellContents',
        skipHeader: false
    };

    grandTotalRow: 'top' | 'bottom' | 'pinnedTop' | 'pinnedBottom' | undefined = 'pinnedBottom';

    /* Used to set the Default grouping type. Can be changed on the fly using the GridAPI after initialization. */
    groupDisplayType: 'singleColumn' | 'multipleColumns' = 'singleColumn';

    /* Expands the first group by default */
    groupDefaultExpanded = -1; /* 0 for none, 1 for first level only, -1 to expand everything */

    sideBar: SideBarDef | string | string[] | boolean | null = {
        toolPanels: ['columns', 'filters-new']
    };

    rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
        mode: 'singleRow',
        checkboxes: false,
        enableClickSelection: true
    };

    /* ToDo: Review further */
    // theme = themeQuartz.withParams({
    theme = signal(themeBalham.withParams({
        backgroundColor: 'var(--report-data-bg)',
        foregroundColor: 'var(--p-content-color)',
        accentColor: 'var(--p-primary-color)',
        oddRowBackgroundColor: 'var(--p-datatable-row-striped-background)',
        rowHoverColor: 'var(--p-content-hover-background)',
        selectedRowBackgroundColor: 'var(--p-highlight-background)',
        borderColor: 'var(--p-content-border-color)',
        columnBorder: false,

        fontSize: '12px',
        fontFamily: 'inherit',
        textColor: 'var(--p-text-color)',

        headerBackgroundColor: 'var(--report-grid-header-bg)',
        headerTextColor: 'var(--p-text-muted-color)',
        headerFontWeight: 600,
        wrapperBorderRadius: '0'
    }));

    suppressAggFuncInHeader = true;

    defaultColDef = {
        filter: 'agTextColumnFilter'
    };


    columnTypes: Record<SupportedColumnType, ColTypeDef> = {
        boolean: {
            cellRenderer: CheckboxRendererComponent,
            filter: 'agSetColumnFilter',
            filterParams: {
                suppressClearModelOnRefreshValues: true,
                comparator: (a: any, b: any) => {
                    const aBool = a === true || a === 'true';
                    const bBool = b === true || b === 'true';

                    if (aBool === bBool) {
                        return 0;
                    }

                    return aBool ? -1 : 1;
                }
            }
            // valueGetter is added by the Grid Service since a field name is required
        },
        currency: {
            aggFunc: 'sum',
            cellClass: 'ag-right-aligned-cell',
            filter: 'agNumberColumnFilter',
            headerClass: 'ag-right-aligned-header',
            useValueFormatterForExport: false

            // valueFormatter is added by the Grid Service to support decimalPlaces
        },
        date: {
            filter: 'agDateColumnFilter',
            filterParams: {
                useIsoDateFormat: true
            },
            cellClass: 'ag-right-aligned-cell',
            headerClass: 'ag-right-aligned-header',
            valueFormatter: valueFormatters.date,
            
            // valueGetter is added by the Grid Service since a field name is required
        },
        dateTime: {
            filter: 'agSetColumnFilter',
            filterParams: {
                suppressClearModelOnRefreshValues: true,
                useIsoDateFormat: true
            },
            cellClass: 'ag-right-aligned-cell',
            headerClass: 'ag-right-aligned-header',
            valueFormatter: valueFormatters.dateTime
            // valueGetter is added by the Grid Service since a field name is required
        },
        dateMonth: {
            filter: 'agDateColumnFilter',
            comparator: comparators.month,
            // valueGetter is added by the Grid Service since a field name is required
        },
        dateYear: {
            cellClass: 'ag-right-aligned-cell',
            filter: 'agNumberColumnFilter',
            headerClass: 'ag-right-aligned-header',
            aggFunc: 'agSetColumnFilter'
            // valueGetter is added by the Grid Service since a field name is required
        },
        percentage: {
            aggFunc: 'avg',
            cellClass: 'ag-right-aligned-cell',
            filter: 'agNumberColumnFilter',
            headerClass: 'ag-right-aligned-header',
            useValueFormatterForExport: false
            // valueFormatter is added by the Grid Service to support decimalPlaces
            // filterValueGetter is added by the Grid Service since a field name is required
        },
        number: {
            aggFunc: 'sum',
            initialAggFunc: 'sum',
            defaultAggFunc: 'sum',
            cellClass: 'ag-right-aligned-cell',
            filter: 'agNumberColumnFilter',
            headerClass: 'ag-right-aligned-header',
            enableValue: true,
            useValueFormatterForExport: false
            // valueFormatter is added by the Grid Service to support decimalPlaces
        },
        text: {
            filter: 'agTextColumnFilter'
        },
        time: {
            cellClass: 'ag-right-aligned-cell',
            valueFormatter: valueFormatters.time,
            filter: false
        }
    };
    
    constructor() {
        /* Clear Filters */
        effect(() => {
            this.reportWorkspace.clearFiltersRequest();

            if(!this.gridApi) {
                return;
            }

            this.gridApi.setFilterModel(null);
            untracked(() => {
                this.initialColumnFilters.set(null);
            });
        });

        /* Expand All */
        effect(() => {
            this.reportWorkspace.expandAllRequest();

            if (!this.gridApi) {
                return;
            }

            this.gridApi.expandAll();
        });

        /* Collapse All */
        effect(() => {
            this.reportWorkspace.collapseAllRequest();
            
            if (!this.gridApi) {
                return;
            }

            this.gridApi.collapseAll();
        });

        /* Autosize Columns */
        effect(() => {
            this.reportWorkspace.autosizeColumnsRequest();
            
            if (!this.gridApi) {
                return;
            }

            /* ToDo: Get skipHeader from "Report Options" */
            const skipHeader = undefined;

            this.gridApi.autoSizeAllColumns(skipHeader);
        });

        /* Search Term Changed */
        effect(() => {
            const searchTerm = this.reportWorkspace.searchTerm();

            if (!this.gridApi) {
                return;
            }

            this.gridApi.setGridOption('quickFilterText', searchTerm);
        });

        /* Export Request*/
        effect(() => {
            const exportRequest = this.reportWorkspace.exportRequest();

            if (exportRequest === null) {
                return;
            }

            this.exportFile(exportRequest);
            
            this.reportWorkspace.setExportRequest(null);
        });

        /* Active Layout Changed */
        effect(() => {
            this.reportWorkspace.activeLayoutKey();
            this.reportWorkspace.refreshLayoutRequest(); // Reapply the Layout when requested

            untracked(() => {
                const columnDefinitions = this.columnDefinitions();
                const columnGroupDefinitions = this.columnGroupDefinitions();
                const subscriber = this.subscriber();
                const includeAllColumns = this.reportWorkspace.includeAllColumns();

                const colDefs = this.gridService.getAgGridColDefs(columnDefinitions, columnGroupDefinitions, subscriber, includeAllColumns);
                this.colDefs.set(colDefs);
            });
        });
        
        /* Filter Set changed */
        effect(() => {
            const isGridReady = this.gridReady();
            const filterSetKey = this.reportWorkspace.activeFilterSetKey();
            this.reportWorkspace.refreshFiltersRequest(); // Reapply the Filter Set when requested

            if (!isGridReady) {
                return;
            }

            if (!filterSetKey) {
                return;
            }

            const filterModel = (untracked(this.reportWorkspace.filterSets).find(f => f.itemKey === filterSetKey)?.itemDefinition?.filterModel ?? null) as ColumnFilters | null;
            void this.applyFilterModel(filterModel);
        });
    }

    ngOnInit(): void {
        const columnDefinitions = this.columnDefinitions();

        const initialFilters = this.gridService.getColumnFiltersFromQueryParams(columnDefinitions, this.columnQueryDefaults());
        this.initialColumnFilters.set(initialFilters);     
    }


    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.gridReady.set(true);

        /* Register a method to compute the Layout Definition using the current Grid State */
        this.reportWorkspace.registerLayoutDefinitionProvider(() => {
            const state = this.gridApi.getState();
            const reportColumnDefinitions = this.reportWorkspace.baseColumnDefinitions();
            
            const definition = this.gridService.getLayoutDefinitionFromGridState(state, reportColumnDefinitions);

            return definition;
        });

        /* Register a method to rebuild the grid state. Typically occurs if a SysAdmin changes the Subscriber */
        this.reportWorkspace.registerColumnDefinitionsStaleHandler(() => {
            const layoutDefinition = this.getLayoutDefinitionFromGridState();
            const columnGroupDefinitions = this.columnGroupDefinitions();
            const subscriber = this.subscriber();
            const includeAllColumns = this.reportWorkspace.includeAllColumns();

            const columnDefinitions = this.reportWorkspace.getColumnDefinitionsFromLayoutDefinition(layoutDefinition) ?? [];

            const colDefs = this.gridService.getAgGridColDefs(columnDefinitions, columnGroupDefinitions, subscriber, includeAllColumns);
            this.colDefs.set(colDefs);
            this.gridApi.updateGridOptions({columnDefs: colDefs});
        });

        /* Register a method to compute the Filter Set Definition using the current Grid State */
        this.reportWorkspace.registerFilterSetDefinitionProvider(() => {
            return this.gridApi.getFilterModel();
        })
    }

    private getLayoutDefinitionFromGridState() {
        const state = this.gridApi.getState();
        const reportColumnDefinitions = this.reportWorkspace.baseColumnDefinitions();

        const definition = this.gridService.getLayoutDefinitionFromGridState(state, reportColumnDefinitions);
        
        return definition;
    }

    onFirstDataRendered(params: FirstDataRenderedEvent) {
        const initialColumnFilters = this.initialColumnFilters();
        if (initialColumnFilters) {
            void this.applyFilterModel(initialColumnFilters);
            this.initialColumnFilters.set(null);
        }
    }

    onFilterChanged(params: FilterChangedEvent) {
        const columnFilters = this.gridApi.getFilterModel();

        const anyFiltersActive = Object.keys(columnFilters).length > 0;

        this.reportWorkspace.setReportFiltersActive(anyFiltersActive);
    }

    private async applyFilterModel(filterModel: ColumnFilters | null) {
        if (filterModel == null) {
            this.gridApi.setFilterModel(null);
            return;
        }

        this.gridApi.setFilterModel(null);
        await Promise.all(
            Object.entries(filterModel).map(([columnKey, filter]) =>
                this.gridApi.setColumnFilterModel(columnKey, filter)
            )
        );
        this.gridApi.onFilterChanged();
    }

    /* Exports */
    exportFile(type: ExportOptions) {
        if (!this.gridApi) {
            return;
        }

        const fileName = this.fileNameService.buildReportFileName(
            this.isSysAdmin(),
            this.subscriber()?.subscriberName ?? '',
            this.reportName()
        );

        if (type === 'EXCEL') {
            this.gridApi.exportDataAsExcel({fileName: fileName});
            return;
        }

        this.gridApi.exportDataAsCsv({fileName: fileName});
    }    
}
